import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

/**
 * Internal share management (2026-08-23) — modal for creating, listing,
 * copying, and revoking INTERNAL links on a trailer or Decision Room.
 *
 * Visual contract: everything here is amber-badged INTERNAL so it can
 * never be confused with the buyer share flow next to it. Links route
 * to /i/<token> (the internal viewer), not /share/<token>.
 */
export default function InternalSharePanel({ artifactType, artifactId, authToken, onClose }) {
  const [shares, setShares] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [expiryDays, setExpiryDays] = useState(14);
  const [canComment, setCanComment] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_BASE}/api/internal-shares?artifactType=${artifactType}&artifactId=${encodeURIComponent(artifactId)}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).error || `Failed (${r.status})`);
        setShares((await r.json()).shares);
      })
      .catch((e) => setError(e.message));
  }, [artifactType, artifactId, authToken]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!label.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/internal-shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ artifactType, artifactId, recipientLabel: label.trim(), expiryDays, canComment }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed (${r.status})`);
      setLabel(''); setCanComment(false);
      load();
      copyLink(data.share);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/api/internal-shares/${id}/revoke`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` },
      });
      load();
    } finally { setBusy(false); }
  };

  const copyLink = (share) => {
    const url = `${window.location.origin}/i/${share.token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 1800);
    }).catch(() => {});
  };

  const daysLeft = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    const d = Math.ceil(ms / 86_400_000);
    return d === 1 ? '1 day left' : `${d} days left`;
  };

  const live = (s) => !s.revokedAt && new Date(s.expiresAt).getTime() > Date.now();

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--room-card, #fff)', borderRadius: 16, padding: 24, maxWidth: 560, width: '100%', maxHeight: 'calc(100vh - 48px)', overflow: 'auto', boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>Share internally</h2>
          <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '2px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>INTERNAL</span>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--room-text-muted, #64748b)' }}>✕</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.5 }}>
          For colleagues without a seat. Internal viewers see the AI's reasoning{artifactType === 'buyer_space' ? ' and internal-only plan items' : ''};
          their views never count toward buyer engagement. Links expire automatically.
          <b style={{ color: '#92400e' }}> Never send an internal link to a prospect.</b>
        </p>

        {/* Create */}
        <div style={{ background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='Who is this for? e.g. "Sarah — CS lead"'
              style={{ flex: '1 1 200px', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', fontSize: 13, background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)' }}
            />
            <select value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', fontSize: 13, background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)' }}>
              <option value={7}>Expires in 7 days</option>
              <option value={14}>Expires in 14 days</option>
              <option value={30}>Expires in 30 days</option>
              <option value={90}>Expires in 90 days</option>
            </select>
            <button type="button" onClick={create} disabled={busy || !label.trim()}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: !label.trim() ? 0.6 : 1 }}>
              Create &amp; copy
            </button>
          </div>
          {artifactType === 'demo' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12.5, color: 'var(--room-text-body, #475569)', cursor: 'pointer' }}>
              <input type="checkbox" checked={canComment} onChange={(e) => setCanComment(e.target.checked)} />
              Allow this viewer to leave comments (lands in the trailer's internal thread; you'll be notified)
            </label>
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626', fontSize: 12.5 }}>{error}</div>
        )}

        {/* List */}
        {shares === null ? (
          <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading…</div>
        ) : shares.length === 0 ? (
          <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>No internal links yet.</div>
        ) : (
          shares.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 4px', borderBottom: '1px solid var(--room-border-soft, #f1f5f9)', fontSize: 13, opacity: live(s) ? 1 : 0.55 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: 'var(--room-text-strong, #0f172a)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.recipientLabel}
                  {s.canComment && <span style={{ fontWeight: 400, color: 'var(--room-text-muted, #64748b)' }}> · comments on</span>}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--room-text-muted, #64748b)' }}>
                  {s.revokedAt ? 'revoked'
                    : `${daysLeft(s.expiresAt)} · ${s.viewCount || 0} view${(s.viewCount || 0) === 1 ? '' : 's'}${s.lastViewedAt ? ` · last ${new Date(s.lastViewedAt).toLocaleDateString()}` : ''}`}
                  {' · by '}{s.creatorName || 'unknown'}
                </div>
              </div>
              {live(s) && (
                <>
                  <button type="button" onClick={() => copyLink(s)}
                    style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: copiedId === s.id ? '#166534' : 'var(--room-text-body, #475569)', fontSize: 12, cursor: 'pointer' }}>
                    {copiedId === s.id ? 'Copied ✓' : 'Copy link'}
                  </button>
                  <button type="button" onClick={() => revoke(s.id)} disabled={busy}
                    style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.35)', background: 'var(--room-card, #fff)', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>
                    Revoke
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
